import type { Line, Segment, Theme } from "../termui";
import type { MarkdownThemeOverrides } from "./types";

function headingLevelKey(level: number): keyof Theme["markdown"]["headings"] {
  if (level <= 1) return "h1";
  if (level === 2) return "h2";
  if (level === 3) return "h3";
  if (level === 4) return "h4";
  if (level === 5) return "h5";
  return "h6";
}

export function headingColor(level: number, theme: Theme, overrides?: MarkdownThemeOverrides): string {
  const key = headingLevelKey(level);
  return overrides?.headings?.[key] ?? theme.markdown.headings[key];
}

export function isNumericValue(value: unknown): boolean {
  if (typeof value === "number") return Number.isFinite(value);
  if (typeof value !== "string") return false;
  const trimmed = value.trim();
  return /^-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?$/.test(trimmed);
}

export function wrapText(text: string, maxWidth: number): string[] {
  const width = Math.max(1, maxWidth);
  const normalized = text.replace(/\s+/g, " ").trim();
  if (!normalized) return [""];

  const linesOut: string[] = [];
  let rest = normalized;

  while (rest.length > width) {
    const slice = rest.slice(0, width + 1);
    const splitAt = slice.lastIndexOf(" ");
    const end = splitAt > 0 ? splitAt : width;
    linesOut.push(rest.slice(0, end).trimEnd());
    rest = rest.slice(end).trimStart();
  }

  linesOut.push(rest);
  return linesOut;
}

export function renderPrefixedText(
  text: string,
  width: number,
  prefix: Segment,
  continuationPrefix: string,
  textStyle?: Omit<Exclude<Segment, string>, "text" | "onClick">,
): Line[] {
  const prefixText = typeof prefix === "string" ? prefix : prefix.text;
  const wrapped = wrapText(text, width - prefixText.length);

  return wrapped.map((chunk, index) => {
    if (index === 0) {
      return [prefix, textStyle ? { text: chunk, ...textStyle } : chunk];
    }
    return [continuationPrefix, textStyle ? { text: chunk, ...textStyle } : chunk];
  });
}

export function normalizeHtmlText(text: string): string {
  return text.replace(/\s+/g, " ").trim();
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

function getValueByPath(source: unknown, path: string): unknown {
  const parts = path.split(".");
  let current: unknown = source;
  for (const part of parts) {
    if (!isRecord(current)) return undefined;
    current = current[part];
  }
  return current;
}

export function resolveThemeColor(value: string | null | undefined, theme: Theme): string | undefined {
  if (!value) return undefined;
  const raw = value.trim();
  if (!raw) return undefined;
  const topLevel = theme[raw as keyof Theme];
  if (typeof topLevel === "string") return topLevel;
  const nested = getValueByPath(theme, raw);
  if (typeof nested === "string") return nested;
  return raw;
}

export function getColorAttr(el: Element, theme: Theme, names: string[]): string | undefined {
  for (const name of names) {
    const value = resolveThemeColor(el.getAttribute(name), theme);
    if (value) return value;
  }
  return undefined;
}

export function parseIntegerAttr(el: Element, name: string): number | undefined {
  const raw = el.getAttribute(name);
  if (!raw) return undefined;
  const n = Number.parseInt(raw.trim(), 10);
  return Number.isFinite(n) ? n : undefined;
}

export function parseCodeLanguage(el: Element): string | undefined {
  const explicit = el.getAttribute("language");
  if (explicit && explicit.trim()) return explicit.trim();

  const klass = el.getAttribute("class") ?? "";
  const match = klass.match(/\blanguage-([\w+-]+)\b/);
  return match?.[1];
}
